import React, { useState, useMemo, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Select, SelectItem, Card } from '@nextui-org/react';
import { COLORS } from 'common/src/utils/colors';
import MultiSelectWithActions from '../../common/MultiSelectWithActions';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import {
  SettlementSalesData,
  SettlementMethodology,
  SettlementGroupBy,
  SettlementDateField,
} from '../../../types/settlements';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import {
  getAvailableVarieties,
  getSettlementsSalesVsExportersDispersion,
} from 'common/src/api/settlements';
import DispertionChartSkeleton from './DispertionChartSkeleton';
import { useDebounce } from '../../../hooks/useDebounce';
import { SalesChartTable, SalesExcelButton } from './SalesChartTable';
import { Alert } from '../../ui';

interface DispertionChartProps {
  methodology: SettlementMethodology;
  groupBy: SettlementGroupBy;
  dateField: SettlementDateField;
  dateRange: [Date, Date] | null;
  selectedMarkets: string[];
  visualization?: 'chart' | 'table';
}

const DispertionChart: React.FC<DispertionChartProps> = ({
  methodology,
  groupBy,
  dateField,
  dateRange,
  selectedMarkets,
  visualization = 'chart',
}) => {
  const { t } = useTranslation('settlementsComparative');
  // Internal state for component
  const [availableVarieties, setAvailableVarieties] = useState<
    { variety: string; total_kilos: number }[]
  >([]);
  const [selectedVariety, setSelectedVariety] = useState('');
  const [selectedCalibers, setSelectedCalibers] = useState<string[]>([]);
  const [separation, setSeparation] = useState<
    'none' | 'by_label' | 'by_receiver'
  >('none');

  // Data and loading states
  const [data, setData] = useState<SettlementSalesData | null>(null);
  const [currency, setCurrency] = useState<string>('USD');
  const [totalKilos, setTotalKilos] = useState<number | null>(null);
  const [varietiesLoading, setVarietiesLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { session, userProfile, darkMode } = useUserStore();
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();

  // Check if user is demo
  const isDemo = !!userProfile?.exporter?.hasDemo;

  // Debounce filters to avoid too many API calls
  const debouncedSelectedMarkets = useDebounce(selectedMarkets, 500); // 500ms delay
  const debouncedSelectedVariety = useDebounce(selectedVariety, 300); // 300ms delay
  const debouncedSeparation = useDebounce(separation, 300); // 300ms delay

  const userExporter = userProfile?.exporter;

  // Separation options
  const separationOptions = [
    { key: 'none', label: t('Sin separación') },
    { key: 'by_label', label: t('Por Etiqueta') },
    { key: 'by_receiver', label: t('Por Cliente') },
  ];

  // Fetch available varieties
  const fetchAvailableVarieties = async () => {
    if (
      !session ||
      !selectedSeason ||
      !selectedSpecies ||
      selectedSpecies.id !== 2
    ) {
      return;
    }

    setVarietiesLoading(true);
    setError(null);

    const startDate = dateRange?.[0]?.toISOString().split('T')[0];
    const endDate = dateRange?.[1]?.toISOString().split('T')[0];

    try {
      const varieties = await getAvailableVarieties(
        session,
        selectedSeason,
        selectedSpecies.id,
        startDate,
        endDate,
        dateField,
        debouncedSelectedMarkets.length > 0
          ? debouncedSelectedMarkets
          : undefined
      );

      setAvailableVarieties(varieties);

      // Auto-select the most abundant variety (first in the sorted list)
      if (varieties.length > 0 && !selectedVariety) {
        if (isDemo) {
          // For demo users, only select LAPINS if available
          const lapinsVariety = varieties.find(
            (v) => v.variety.toUpperCase() === 'LAPINS'
          );
          if (lapinsVariety) {
            setSelectedVariety(lapinsVariety.variety);
          }
        } else {
          setSelectedVariety(varieties[0].variety);
        }
      }
    } catch (err) {
      setError(t('Error al cargar variedades disponibles'));
      console.error('Error:', err);
    } finally {
      setVarietiesLoading(false);
    }
  };

  // Fetch dispersion data
  const fetchDispersionData = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies ||
      !debouncedSelectedVariety ||
      selectedSpecies.id !== 2
    ) {
      return;
    }

    setDataLoading(true);
    setError(null);

    const startDate = dateRange?.[0]?.toISOString().split('T')[0];
    const endDate = dateRange?.[1]?.toISOString().split('T')[0];

    try {
      const dispersionData = await getSettlementsSalesVsExportersDispersion(
        session,
        userProfile.exporter_id,
        methodology,
        selectedSeason,
        debouncedSelectedVariety,
        debouncedSeparation,
        dateField,
        selectedSpecies.id,
        startDate,
        endDate,
        groupBy,
        debouncedSelectedMarkets.length > 0
          ? debouncedSelectedMarkets
          : undefined
      );

      if (
        dispersionData &&
        typeof dispersionData === 'object' &&
        'data' in dispersionData
      ) {
        // New API response format with currency and total kilos
        setData(dispersionData.data);
        setCurrency(dispersionData.currency || 'USD');
        setTotalKilos(dispersionData.totalKilos || null);
      } else {
        // Fallback for old format
        setData(dispersionData);
        setCurrency('USD');
        setTotalKilos(null);
      }
    } catch (err) {
      setError(t('Error al cargar datos de dispersión'));
      console.error('Error:', err);
    } finally {
      setDataLoading(false);
    }
  };

  // Effect to fetch varieties when filters change
  useEffect(() => {
    fetchAvailableVarieties();
  }, [
    dateRange,
    dateField,
    debouncedSelectedMarkets,
    selectedSeason,
    selectedSpecies,
  ]);

  // Effect to fetch data when variety or separation changes (with debounce)
  useEffect(() => {
    if (debouncedSelectedVariety) {
      fetchDispersionData();
    }
  }, [
    debouncedSelectedVariety,
    debouncedSeparation,
    methodology,
    groupBy,
    dateField,
    dateRange,
    debouncedSelectedMarkets,
    selectedSeason,
    selectedSpecies,
  ]);

  // Reset variety selection when varieties change
  useEffect(() => {
    if (
      availableVarieties.length > 0 &&
      !availableVarieties.find((v) => v.variety === selectedVariety)
    ) {
      setSelectedVariety(availableVarieties[0].variety);
    }
  }, [availableVarieties]);

  // Reset calibers when variety changes
  useEffect(() => {
    setSelectedCalibers([]);
  }, [selectedVariety]);

  const calculatePercentile = (
    value: number | null,
    boxPlotData: {
      min: number;
      q1: number;
      median: number;
      q3: number;
      max: number;
    }
  ) => {
    if (!value) return null;
    if (value >= boxPlotData.max) return 100;
    if (value <= boxPlotData.min) return 0;

    if (value <= boxPlotData.q1) {
      return Math.round(
        ((value - boxPlotData.min) / (boxPlotData.q1 - boxPlotData.min)) * 25
      );
    } else if (value <= boxPlotData.median) {
      return Math.round(
        25 +
          ((value - boxPlotData.q1) / (boxPlotData.median - boxPlotData.q1)) *
            25
      );
    } else if (value <= boxPlotData.q3) {
      return Math.round(
        50 +
          ((value - boxPlotData.median) /
            (boxPlotData.q3 - boxPlotData.median)) *
            25
      );
    } else {
      return Math.round(
        75 +
          ((value - boxPlotData.q3) / (boxPlotData.max - boxPlotData.q3)) * 25
      );
    }
  };

  const processedData = useMemo(() => {
    if (!data || !debouncedSelectedVariety) {
      return null;
    }
    return data[debouncedSelectedVariety];
  }, [data, debouncedSelectedVariety]);

  const availableCalibers = useMemo(() => {
    if (!processedData) return [];
    return [
      ...new Set(
        Object.values(processedData).flatMap((dateKeyData: any) =>
          Object.keys(dateKeyData)
        )
      ),
    ];
  }, [processedData]);

  const prepareChartData = (chartData: any, calibersFilter: string[]) => {
    if (!chartData) {
      return { categories: [], series: [] };
    }

    const dateKeys = Object.keys(chartData).sort();
    const categories: string[] = [];
    const series: any[] = [];

    // First, add the boxplot series for "others"
    const othersData: any[] = [];

    dateKeys.forEach((dateKey) => {
      let calibers = chartData[dateKey];
      if (calibersFilter.length > 0) {
        calibers = Object.fromEntries(
          Object.entries(calibers).filter(([caliber]) =>
            calibersFilter.includes(caliber)
          )
        );
      }

      if (Object.keys(calibers).length === 0) return;
      categories.push(dateKey);

      let othersTotalKilos = 0;
      let othersTotalSales = 0;

      Object.values(calibers).forEach((caliberData: any) => {
        if (caliberData.others && caliberData.others.max > 0) {
          othersTotalKilos += caliberData.othersKg;
          othersTotalSales += caliberData.others.median * caliberData.othersKg;
        }
      });

      const othersAvg =
        othersTotalKilos > 0 ? othersTotalSales / othersTotalKilos : null;

      const othersStats = {
        min: 0,
        q1: 0,
        median: othersAvg,
        q3: 0,
        max: 0,
      };

      const validValues = Object.values(calibers)
        .map((c: any) => c.others)
        .filter((v) => v.max > 0);

      if (validValues.length > 0) {
        othersStats.min = Math.min(...validValues.map((v) => v.min));
        othersStats.max = Math.max(...validValues.map((v) => v.max));
        othersStats.q1 = validValues.map((v) => v.q1).sort((a, b) => a - b)[
          Math.floor(validValues.length / 4)
        ];
        othersStats.q3 = validValues.map((v) => v.q3).sort((a, b) => a - b)[
          Math.floor((validValues.length * 3) / 4)
        ];
      }

      othersData.push({
        x: dateKey,
        y: [
          othersStats.min,
          othersStats.q1,
          othersStats.median,
          othersStats.q3,
          othersStats.max,
        ],
      });
    });

    // Add the boxplot series
    series.push({
      name:
        methodology === 'by_volume' ? t('Mercado') : t('Otras Exportadoras'),
      type: 'boxPlot',
      data: othersData,
    });

    // Now handle own data based on separation
    if (separation === 'none') {
      // Original behavior: single aggregated series
      const ownData: any[] = [];

      dateKeys.forEach((dateKey) => {
        let calibers = chartData[dateKey];
        if (calibersFilter.length > 0) {
          calibers = Object.fromEntries(
            Object.entries(calibers).filter(([caliber]) =>
              calibersFilter.includes(caliber)
            )
          );
        }

        let ownTotalKilos = 0;
        let ownTotalSales = 0;

        Object.values(calibers).forEach((caliberData: any) => {
          if (caliberData.own && caliberData.own.length > 0) {
            ownTotalKilos += caliberData.ownKg;
            ownTotalSales += caliberData.own[0].value * caliberData.ownKg;
          }
        });

        const ownAvg = ownTotalKilos > 0 ? ownTotalSales / ownTotalKilos : null;
        ownData.push({ x: dateKey, y: ownAvg });
      });

      series.push({
        name: userExporter?.name || t('Mi Exportadora'),
        type: 'scatter',
        data: ownData,
      });
    } else {
      // Separated behavior: multiple series, one per identifier
      const identifierData = new Map<string, any[]>();

      // First, collect all unique identifiers across all dates
      const allIdentifiers = new Set<string>();

      dateKeys.forEach((dateKey) => {
        let calibers = chartData[dateKey];
        if (calibersFilter.length > 0) {
          calibers = Object.fromEntries(
            Object.entries(calibers).filter(([caliber]) =>
              calibersFilter.includes(caliber)
            )
          );
        }

        Object.values(calibers).forEach((caliberData: any) => {
          if (caliberData.own && caliberData.own.length > 0) {
            caliberData.own.forEach((ownEntry: any) => {
              const identifier =
                separation === 'by_label'
                  ? ownEntry.label || t('Sin etiqueta')
                  : ownEntry.receiver || t('Sin receptor');
              allIdentifiers.add(identifier);
            });
          }
        });
      });

      // Initialize data arrays for all identifiers with null values for all dates
      allIdentifiers.forEach((identifier) => {
        identifierData.set(
          identifier,
          dateKeys.map((dateKey) => ({ x: dateKey, y: null }))
        );
      });

      // Now fill in the actual data for each identifier and date
      dateKeys.forEach((dateKey, dateIndex) => {
        let calibers = chartData[dateKey];
        if (calibersFilter.length > 0) {
          calibers = Object.fromEntries(
            Object.entries(calibers).filter(([caliber]) =>
              calibersFilter.includes(caliber)
            )
          );
        }

        // Group own data by identifier for this date
        const dateGroupedData = new Map<
          string,
          { totalSales: number; totalKilos: number }
        >();

        Object.values(calibers).forEach((caliberData: any) => {
          if (caliberData.own && caliberData.own.length > 0) {
            caliberData.own.forEach((ownEntry: any) => {
              const identifier =
                separation === 'by_label'
                  ? ownEntry.label || t('Sin etiqueta')
                  : ownEntry.receiver || t('Sin receptor');

              if (!dateGroupedData.has(identifier)) {
                dateGroupedData.set(identifier, {
                  totalSales: 0,
                  totalKilos: 0,
                });
              }

              const group = dateGroupedData.get(identifier)!;
              group.totalSales += ownEntry.value * ownEntry.kilos;
              group.totalKilos += ownEntry.kilos;
            });
          }
        });

        // Update the specific date index for each identifier that has data
        dateGroupedData.forEach((group, identifier) => {
          const avgPrice = group.totalSales / group.totalKilos;
          const seriesData = identifierData.get(identifier)!;
          seriesData[dateIndex] = { x: dateKey, y: avgPrice };
        });
      });

      // Create a series for each identifier
      const colors = [
        '#4CAF50',
        '#2196F3',
        '#FF9800',
        '#9C27B0',
        '#F44336',
        '#009688',
        '#795548',
      ];
      let colorIndex = 0;

      identifierData.forEach((data, identifier) => {
        series.push({
          name: identifier,
          type: 'scatter',
          data: data,
          color: colors[colorIndex % colors.length],
        });
        colorIndex++;
      });
    }

    return {
      categories,
      series: series,
    };
  };

  const { categories, series } = prepareChartData(
    processedData,
    selectedCalibers
  );

  const createTooltip = (dataPointIndex: number) => {
    const originalDateKey = categories[dataPointIndex];
    const dateData = processedData?.[originalDateKey];

    if (!dateData) {
      return `<div style="padding: 10px;">${t('Sin datos para')} ${originalDateKey}</div>`;
    }

    // Format the date consistently with X-axis
    let formattedDate = originalDateKey;
    if (groupBy === 'week') {
      const parts = originalDateKey.split('-W');
      if (parts.length > 1) {
        formattedDate = `${t('Semana')} ${parts[1]}`;
      }
    } else {
      // For daily format: YYYY-MM-DD -> DD/MM
      const parts = originalDateKey.split('-');
      if (parts.length === 3) {
        formattedDate = `${parts[2]}/${parts[1]}`;
      }
    }

    const calibersToAggregate =
      selectedCalibers.length > 0 ? selectedCalibers : Object.keys(dateData);

    const filteredCalibers = Object.fromEntries(
      Object.entries(dateData).filter(([caliber]) =>
        calibersToAggregate.includes(caliber)
      )
    );

    // Calculate aggregated statistics
    let ownTotalKilos = 0;
    let ownTotalSales = 0;
    let othersTotalKilos = 0;
    let othersTotalSales = 0;
    const allOthersValues: number[] = [];

    // Group own data by identifier (label or receiver)
    const groupedOwnData = new Map<
      string,
      { totalSales: number; totalKilos: number }
    >();

    Object.values(filteredCalibers).forEach((caliberData: any) => {
      if (caliberData.own && caliberData.own.length > 0) {
        ownTotalKilos += caliberData.ownKg;
        caliberData.own.forEach((ownEntry: any) => {
          ownTotalSales += ownEntry.value * ownEntry.kilos;

          // Group by identifier for tooltip
          const identifier =
            separation === 'by_label'
              ? ownEntry.label || t('Sin etiqueta')
              : separation === 'by_receiver'
                ? ownEntry.receiver || t('Sin receptor')
                : t('PROPIO');

          if (!groupedOwnData.has(identifier)) {
            groupedOwnData.set(identifier, { totalSales: 0, totalKilos: 0 });
          }

          const group = groupedOwnData.get(identifier)!;
          group.totalSales += ownEntry.value * ownEntry.kilos;
          group.totalKilos += ownEntry.kilos;
        });
      }
      if (caliberData.others && caliberData.othersKg > 0) {
        othersTotalKilos += caliberData.othersKg;
        othersTotalSales += caliberData.others.median * caliberData.othersKg;
        allOthersValues.push(
          caliberData.others.min,
          caliberData.others.q1,
          caliberData.others.median,
          caliberData.others.q3,
          caliberData.others.max
        );
      }
    });

    const ownAvg = ownTotalKilos > 0 ? ownTotalSales / ownTotalKilos : null;
    const othersAvg =
      othersTotalKilos > 0 ? othersTotalSales / othersTotalKilos : null;

    const aggregatedOthersStats =
      allOthersValues.length > 0
        ? {
            min: Math.min(...allOthersValues),
            q1: allOthersValues.sort((a, b) => a - b)[
              Math.floor(allOthersValues.length * 0.25)
            ],
            median: othersAvg || 0,
            q3: allOthersValues.sort((a, b) => a - b)[
              Math.floor(allOthersValues.length * 0.75)
            ],
            max: Math.max(...allOthersValues),
          }
        : null;

    // Determinar el tamaño del tooltip según la separación y cantidad de datos
    const clientCount = separation === 'by_receiver' ? groupedOwnData.size : 0;
    const needsOptimization = separation === 'by_receiver' && clientCount > 3;

    // Configuraciones dinámicas del tooltip
    const tooltipConfig = needsOptimization
      ? {
          maxWidth: '300px',
          padding: '8px',
          fontSize: '10px',
          titleSize: '12px',
          spacing: '3px',
        }
      : {
          maxWidth: '400px',
          padding: '12px',
          fontSize: '12px',
          titleSize: '14px',
          spacing: '6px',
        };

    const caliberText =
      selectedCalibers.length > 0
        ? `${t('Calibres')}: ${selectedCalibers.join(', ')}`
        : `${t('Todos los calibres')} (${Object.keys(dateData).join(', ')})`;

    // Generate own data section based on separation
    const ownValuesSection =
      groupedOwnData.size > 0
        ? separation === 'none'
          ? `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: ${
            tooltipConfig.spacing
          }; padding: ${
            needsOptimization ? '4px' : '8px'
          }; background: #f8fdf8; border-radius: 6px;">
            <span style="color: #4CAF50; font-weight: bold; font-size: ${
              tooltipConfig.fontSize
            };">${t('Tus datos')}:</span>
            <span style="font-size: ${
              tooltipConfig.fontSize
            };"><strong>$${ownAvg?.toFixed(
              2
            )}</strong> • ${ownTotalKilos.toLocaleString('es-CL')} kg</span>
          </div>
        `
          : Array.from(groupedOwnData.entries())
              .map(([identifier, group]) => {
                const avgPrice = group.totalSales / group.totalKilos;

                // Calculate percentile for this specific identifier
                const percentile = aggregatedOthersStats
                  ? calculatePercentile(avgPrice, aggregatedOthersStats)
                  : null;

                return needsOptimization
                  ? `
              <div style="margin-bottom: 3px; padding: 3px; background: #f8fdf8; border-radius: 3px; border-left: 3px solid #4CAF50;">
                <div style="font-weight: bold; font-size: 11px; color: #4CAF50; margin-bottom: 1px;">${identifier}</div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 10px;">
                  <span><strong>$${avgPrice.toFixed(
                    2
                  )}</strong> • ${group.totalKilos.toLocaleString(
                    'es-CL'
                  )} kg</span>
                  ${
                    percentile !== null
                      ? `<span style="font-weight: bold; color: #FF5722;">P${percentile}</span>`
                      : ''
                  }
                </div>
              </div>
            `
                  : `
              <div style="margin-bottom: ${
                tooltipConfig.spacing
              }; padding: 8px; background: #f8fdf8; border-radius: 6px; border-left: 4px solid #4CAF50;">
                <div style="font-weight: bold; font-size: ${
                  tooltipConfig.fontSize
                }; color: #4CAF50; margin-bottom: 4px;">${identifier}</div>
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: ${
                  tooltipConfig.fontSize
                };">
                  <span><strong>$${avgPrice.toFixed(
                    2
                  )}</strong> • ${group.totalKilos.toLocaleString(
                    'es-CL'
                  )} kg</span>
                  ${
                    percentile !== null
                      ? `<span style="font-weight: bold; color: #FF5722; background: #fff3e0; padding: 2px 6px; border-radius: 3px;">${t('Percentil')} ${percentile}%</span>`
                      : ''
                  }
                </div>
              </div>
            `;
              })
              .join('')
        : `
        <div style="font-size: ${tooltipConfig.fontSize}; color: #666; text-align: center; padding: 8px;">
          ${t('No hay datos propios')}
        </div>
      `;

    let html = `
      <div style="background: white; padding: ${
        tooltipConfig.padding
      }; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); font-family: Arial, sans-serif; overflow-y: auto; max-width: ${
        tooltipConfig.maxWidth
      }; line-height: 1.3;" 
           onwheel="event.stopPropagation();" 
           onscroll="event.stopPropagation();"
           ontouchmove="event.stopPropagation();">
        <div style="font-weight: bold; margin-bottom: ${
          tooltipConfig.spacing
        }; color: #333; font-size: ${
          tooltipConfig.titleSize
        }; position: sticky; top: 0; background: white; z-index: 1; border-bottom: 1px solid #eee; padding-bottom: ${
          needsOptimization ? '3px' : '6px'
        };">
          ${formattedDate} - ${debouncedSelectedVariety}
        </div>
        <div style="margin-bottom: ${tooltipConfig.spacing}; font-size: ${
          needsOptimization ? '10px' : tooltipConfig.fontSize
        }; color: #666;">
          ${caliberText}
        </div>
        ${
          separation !== 'none'
            ? `
        <div style="margin-bottom: ${tooltipConfig.spacing}; font-size: ${
          needsOptimization ? '10px' : tooltipConfig.fontSize
        }; color: #2196F3; font-weight: bold;">
          ${t('Separado por')} ${separation === 'by_label' ? t('Etiqueta') : t('Cliente')}
        </div>
        `
            : ''
        }
        ${ownValuesSection}
    `;

    // Add percentile info for single own value (no separation)
    if (separation === 'none' && ownAvg !== null && aggregatedOthersStats) {
      const percentile = calculatePercentile(ownAvg, aggregatedOthersStats);
      if (percentile !== null) {
        html += `
          <div style="font-size: ${
            tooltipConfig.fontSize
          }; color: #4CAF50; margin-top: ${
            tooltipConfig.spacing
          }; text-align: center; background: #f0f9f0; padding: ${
            needsOptimization ? '2px 4px' : '4px 8px'
          }; border-radius: 4px;">
            <strong>${t('Percentil')}: ${percentile}%</strong>
          </div>
        `;
      }
    }

    // Add market stats summary
    if (aggregatedOthersStats && othersTotalKilos > 0) {
      const othersLabel =
        methodology === 'by_volume' ? t('Mercado') : t('Otras Exportadoras');
      html += `
        <div style="border-top: 1px solid #eee; margin: ${
          tooltipConfig.spacing
        } 0; padding-top: ${needsOptimization ? '4px' : '6px'};">
          <div style="font-size: ${
            tooltipConfig.fontSize
          }; color: #333; font-weight: bold; margin-bottom: ${
            needsOptimization ? '3px' : '4px'
          };">
            ${othersLabel} (${othersTotalKilos.toLocaleString('es-CL')} kg)
          </div>
          <div style="font-size: ${
            needsOptimization ? '10px' : '11px'
          }; color: #666; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 2px; line-height: 1.3;">
            <div>Min: <strong>$${aggregatedOthersStats.min.toFixed(
              1
            )}</strong></div>
            <div>Q1: <strong>$${aggregatedOthersStats.q1.toFixed(
              1
            )}</strong></div>
            <div>Med: <strong>$${aggregatedOthersStats.median.toFixed(
              1
            )}</strong></div>
            <div>Q3: <strong>$${aggregatedOthersStats.q3.toFixed(
              1
            )}</strong></div>
            <div>Max: <strong>$${aggregatedOthersStats.max.toFixed(
              1
            )}</strong></div>
            <div></div>
          </div>
        </div>
      `;
    }

    html += '</div>';
    return html;
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: 'boxPlot',
      height: 400,
      foreColor: darkMode ? '#A1A1AA' : '#71717A',
      zoom: { enabled: false },
    },
    colors:
      series.length > 2
        ? [
            COLORS.cherry,
            '#4CAF50',
            '#2196F3',
            '#FF9800',
            '#9C27B0',
            '#F44336',
            '#009688',
            '#795548',
          ]
        : [COLORS.cherry, COLORS.lightGreen],
    xaxis: {
      categories: categories,
      title: { text: groupBy === 'week' ? t('Semana') : t('Día') },
      labels: {
        formatter: (value) => {
          if (typeof value !== 'string') return value;
          if (groupBy === 'week') {
            const parts = value.split('-W');
            return parts.length > 1 ? parts[1] : value;
          } else {
            const parts = value.split('-');
            if (parts.length === 3) {
              return `${parts[2]}/${parts[1]}`;
            }
            return value;
          }
        },
      },
    },
    yaxis: {
      title: { text: `${t('Precio')} (${currency})` },
      labels: {
        formatter: (value) => {
          if (!value) return '';
          const symbol = currency === 'RMB' ? '¥' : 'USD';
          return `${value.toFixed(2)} ${symbol}`;
        },
      },
    },
    markers: {
      size: series.length > 2 ? 6 : [0, 6], // Use fixed size for multiple series
      hover: {
        size: series.length > 2 ? 7 : 7,
      },
    },
    legend: {
      position: 'bottom',
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function ({ dataPointIndex }) {
        try {
          return createTooltip(dataPointIndex);
        } catch (error) {
          console.error('Tooltip error:', error);
          return `<div style="padding: 10px;">${t('Error al cargar tooltip')}</div>`;
        }
      },
    },
    stroke: {
      colors: [darkMode ? '#ffffff' : '#000000'],
    },
    plotOptions: {
      boxPlot: {
        colors: {
          upper: COLORS.cherry,
          lower: COLORS.cherry,
        },
      },
    } as any,
  };

  // Show loading state
  if (varietiesLoading || dataLoading) {
    return <DispertionChartSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <Alert variant='danger' showIcon>
        {error}
      </Alert>
    );
  }

  // Show empty state if no varieties
  if (availableVarieties.length === 0) {
    return (
      <Card className='p-4'>
        <h3 className='text-lg font-bold mb-4'>
          {t('Análisis de Dispersión')}
        </h3>
        <div className='text-center text-default-400'>
          <p>
            {t(
              'No hay variedades disponibles para el rango de fechas seleccionado'
            )}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className='p-4'>
      <div className='mb-4'>
        <div className='flex items-start justify-between gap-3'>
          <h2 className='text-xl font-bold'>{t('Análisis de Dispersión')}</h2>
          <SalesExcelButton
            categories={categories}
            series={series}
            currency={currency}
            fileName={`precios_dispersion_${debouncedSelectedVariety || 'data'}.xlsx`}
            label={t('Exportar a Excel')}
          />
        </div>
        <p className='text-default-500'>
          {t(
            'Visualización de la dispersión de precios comparando tu exportadora con el mercado'
          )}{' '}
          • {t('Moneda')}: {currency}
        </p>
        {totalKilos && (
          <p className='text-sm text-default-500 mt-1'>
            {t('Muestra')}: {totalKilos.toLocaleString('es-CL')} kg
          </p>
        )}
      </div>

      {/* Controls */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
        {/* Variety Selection */}
        <Select
          label={isDemo ? t('Variedad (Demo: Solo Lapins)') : t('Variedad')}
          selectedKeys={
            selectedVariety ? new Set([selectedVariety]) : new Set()
          }
          onSelectionChange={(keys) => {
            if (!isDemo) {
              const selected = Array.from(keys)[0] as string;
              if (selected) setSelectedVariety(selected);
            }
          }}
          className='w-full'
          isDisabled={isDemo}
          size='md'
        >
          {(isDemo
            ? availableVarieties.filter(
                (v) => v.variety.toUpperCase() === 'LAPINS'
              )
            : availableVarieties
          ).map((variety) => (
            <SelectItem
              key={variety.variety}
              value={variety.variety}
              textValue={variety.variety}
            >
              {variety.variety}
            </SelectItem>
          ))}
        </Select>
        {/* Caliber Selection */}
        <MultiSelectWithActions
          label={t('Calibres')}
          placeholder={t('Todos')}
          options={availableCalibers.map((caliber) => ({
            value: caliber,
            label: caliber,
          }))}
          selectedKeys={selectedCalibers}
          onSelectionChange={setSelectedCalibers}
          size='md'
          className='w-full'
        />
        {/* Separation Selection */}
        <Select
          label={t('Separación')}
          value={separation}
          onChange={(e) =>
            setSeparation(e.target.value as 'none' | 'by_label' | 'by_receiver')
          }
          defaultSelectedKeys={[separation]}
          className='w-full'
          size='md'
        >
          {separationOptions.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.label}
            </SelectItem>
          ))}
        </Select>
      </div>

      {/* Chart */}
      {dataLoading ? (
        <div className='flex items-center justify-center h-64'>
          <p className='text-default-400'>
            {t('Cargando datos de dispersión...')}
          </p>
        </div>
      ) : data && processedData && visualization === 'table' ? (
        <SalesChartTable
          categories={categories}
          series={series}
          currency={currency}
          emptyLabel={t('No hay datos disponibles')}
        />
      ) : data && processedData ? (
        <ReactApexChart
          options={chartOptions}
          series={series}
          type='boxPlot'
          height={400}
        />
      ) : (
        <div className='flex items-center justify-center h-64'>
          <p className='text-default-400'>{t('No hay datos disponibles')}</p>
        </div>
      )}
    </Card>
  );
};

export default DispertionChart;
